import React, { useState } from 'react';
import { twMerge } from 'tailwind-merge';
import useAppContext from 'flow/hooks/useAppContext';
import useExtension from 'flow/hooks/useExtension';
import { useAppSelector } from 'flow/hooks/useRedux';
import ContactAvatar from 'flow/components/ui-components/ContactAvatar';
import Button from 'flow/components/ui-components/Button';
import { selectChatSessionAdmins } from 'flow/selectors/chat-selectors';
import {
    selectActiveMeetingChatHistoryActivated,
    selectActiveMeetingChatRoomHasMultipleMembers,
} from 'flow/selectors/meeting-selectors';
import { MeetingMember } from './MeetingParticipantsList/useMeetingMembers';
import AddGuestToRoomModal from './AddGuestToRoomModal';
import { ChatSessionKey } from 'flow/types/EntityKeys';

interface Props {
    roomMembers: MeetingMember[];
    guestMembers: MeetingMember[];
    chatSessionKey: ChatSessionKey;
}

const MemberRow = ({
    member,
    isAdmin,
}: {
    member: MeetingMember;
    isAdmin: boolean;
}) => {
    const { i18next } = useAppContext();

    return (
        <div className="flex items-center gap-x-3 px-4 py-2">
            <div className="relative shrink-0">
                <ContactAvatar
                    contact={member.contact}
                    size="medium"
                    lazyLoad={false}
                />
                <span
                    className={twMerge(
                        'absolute end-0 bottom-0 size-2.5 rounded-full border-2 border-gray-800',
                        member.audioActive ? 'bg-green-500' : 'bg-red-500',
                    )}
                />
            </div>
            <div className="flex min-w-0 flex-1 flex-col">
                <span className="truncate text-sm font-medium text-white">
                    {member.displayName}
                    {member.memberIsSelf && (
                        <span className="text-gray-500">
                            {` (${i18next.t('chat.you')})`}
                        </span>
                    )}
                </span>
                {isAdmin && (
                    <span className="text-xs text-red-500">
                        {i18next.t('chat.admin')}
                    </span>
                )}
            </div>
        </div>
    );
};

const GuestRow = ({
    member,
    canAdd,
    onAdd,
}: {
    member: MeetingMember;
    canAdd: boolean;
    onAdd: (member: MeetingMember) => void;
}) => {
    const { i18next } = useAppContext();

    return (
        <div className="flex items-center gap-x-3 px-4 py-2">
            <ContactAvatar
                contact={member.contact}
                size="medium"
                lazyLoad={false}
            />
            <div className="flex min-w-0 flex-1 flex-col">
                <span className="truncate text-sm font-medium text-white">
                    {member.displayName}
                </span>
                <span className="text-xs text-gray-400">
                    {i18next.t(
                        member.isInternal
                            ? 'meetings.participants.internal_guest'
                            : 'meetings.participants.external_guest',
                    )}
                </span>
            </div>
            {canAdd && (
                <Button
                    size="small"
                    color="basic"
                    className="shrink-0 !rounded-lg !bg-gray-700 px-3 py-1.5 !text-white hover:!bg-gray-600"
                    onClick={() => onAdd(member)}
                >
                    {i18next.t('meetings.participants.add_to_room')}
                </Button>
            )}
        </div>
    );
};

/**
 * The room members currently live in this call, plus (below a divider) live
 * guests with an Add action — reached from VideoChatSession's members icon,
 * distinct from the video call's own MeetingParticipantsList/
 * MeetingParticipantsPanel. The presence dot reads member.audioActive (mic on
 * = green) since there's no other live status signal already on MeetingMember.
 *
 * Deliberately NOT the room's full persistent membership: `roomMembers` and
 * `guestMembers` come from the live meeting roster (useMeetingMembers), so a
 * room member who isn't currently on the call doesn't appear here at all —
 * "Members (N)" counts who's live and already a room member, not the room's
 * total size. See MessagesMember/MessagesDetailsPage for the actual
 * persistent room member list (with the same Admin badge).
 *
 * Add is internal-guests-only, admin-gated, and hidden until chat history has been activated at least once this session (AC-1685).
 */
const MeetingRoomMembersView = ({
    roomMembers,
    guestMembers,
    chatSessionKey,
}: Props) => {
    const [addingGuest, setAddingGuest] = useState<MeetingMember | null>(null);
    const { ownExtension } = useExtension();
    const chatSessionAdmins = useAppSelector((state) =>
        selectChatSessionAdmins(state, chatSessionKey),
    );
    const chatHistoryActivated = useAppSelector(
        selectActiveMeetingChatHistoryActivated,
    );
    const hasActivatedGroupChat = useAppSelector(
        selectActiveMeetingChatRoomHasMultipleMembers,
    );

    const ownChatUserKey = ownExtension?.get('chatUserKey');
    const viewerIsChatSessionAdmin =
        !!ownChatUserKey && chatSessionAdmins.includes(ownChatUserKey);

    return (
        <div className="flex-1 overflow-auto py-2">
            {roomMembers.map((member) => (
                <MemberRow
                    key={member.memberKey}
                    member={member}
                    isAdmin={
                        !!member.chatUserKey &&
                        chatSessionAdmins.includes(member.chatUserKey)
                    }
                />
            ))}
            {guestMembers.length > 0 && (
                <>
                    {roomMembers.length > 0 && (
                        <hr className="my-1 border-t border-gray-700" />
                    )}
                    {guestMembers.map((member) => (
                        <GuestRow
                            key={member.memberKey}
                            member={member}
                            canAdd={
                                viewerIsChatSessionAdmin &&
                                member.isInternal &&
                                (chatHistoryActivated || hasActivatedGroupChat)
                            }
                            onAdd={setAddingGuest}
                        />
                    ))}
                </>
            )}
            {addingGuest && (
                <AddGuestToRoomModal
                    guestName={addingGuest.displayName}
                    chatSessionKey={chatSessionKey}
                    chatUserKey={addingGuest.chatUserKey}
                    onClose={() => setAddingGuest(null)}
                />
            )}
        </div>
    );
};

export default MeetingRoomMembersView;
